import { useCallback, useEffect, useState } from 'react'
import {
  Button,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Grid,
} from '@mui/material'
import { format, startOfWeek, addDays, addWeeks, subWeeks } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa'
import { metodoPrograma } from '../../../../components/api'
import { setError } from '../../../../components/alerts'
import AutoComplete from '../../../../components/AutoComplete/AutoComplete'
import CartaoDia from './CartaoDia'

const DAY_NAMES = [
  'Segunda-feira',
  'Terça-feira',
  'Quarta-feira',
  'Quinta-feira',
  'Sexta-feira',
  'Sábado',
  'Domingo',
]

const COLUNAS_LISTA = {
  ID_AGENDAMENTO: 0,
  DT_INICIO: 1,
  DT_FIM: 2,
  STATUS: 5,
  DATA_EXCLUSAO: 8,
}

const controller = 'CtrlAberturaAutoagendamento'

const groupIntoRanges = (slots) => {
  if (!slots || slots.length === 0) return []
  const sorted = [...slots].sort((a, b) => a.hour.localeCompare(b.hour))
  const ranges = []
  let current = { start: sorted[0].hour, end: sorted[0].end }

  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].hour === current.end) {
      current.end = sorted[i].end
    } else {
      ranges.push(current)
      current = { start: sorted[i].hour, end: sorted[i].end }
    }
  }
  ranges.push(current)
  return ranges
}

const emptyDays = () =>
  Array.from({ length: 7 }, (_, i) => ({
    idx: i,
    active: false,
    ranges: [],
  }))

const parseSlotFromBackend = (item) => ({
  idAgendamento: item[COLUNAS_LISTA.ID_AGENDAMENTO],
  hour: format(new Date(item[COLUNAS_LISTA.DT_INICIO]), 'HH:mm'),
  end: format(new Date(item[COLUNAS_LISTA.DT_FIM]), 'HH:mm'),
})

const isSlotValido = (item) => item[COLUNAS_LISTA.DATA_EXCLUSAO] === null

const buildSavePayload = (days, weekStart) => {
  const slots = []

  days
    .filter((day) => day.active)
    .forEach((day) => {
      const data = format(addDays(weekStart, day.idx), 'yyyy-MM-dd')
      day.ranges
        .filter((r) => r.type === 'trabalho')
        .forEach((range) => {
          slots.push({
            data,
            dtInicio: `${range.start}:00`,
            dtFim: `${range.end}:00`,
          })
        })
    })

  return { slots }
}

const DisponibilidadeSemanal = ({
  idAgenda,
  onSaved,
  agendaSelecionada,
  onAgendaChange,
}) => {
  const [weekStart, setWeekStart] = useState(() =>
    startOfWeek(new Date(), { weekStartsOn: 1 }),
  )
  const [days, setDays] = useState(emptyDays())
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const carregaSemana = useCallback(
    async (start) => {
      if (!idAgenda) return
      setLoading(true)
      try {
        const promises = Array.from({ length: 7 }, (_, idx) => {
          const data = format(addDays(start, idx), 'yyyy-MM-dd')
          return metodoPrograma('lista', { idAgenda, data }, controller).then(
            (res) => {
              if (!res.status) throw new Error(res.dsErro || 'Erro ao carregar')
              return { dayIdx: idx, lista: res.lista || [] }
            },
          )
        })

        const results = await Promise.all(promises)
        const newDays = emptyDays()

        results.forEach(({ dayIdx, lista }) => {
          const valid = lista.filter(isSlotValido)
          const rawSlots = valid.map(parseSlotFromBackend)

          newDays[dayIdx] = {
            idx: dayIdx,
            active: rawSlots.length > 0,
            ranges: groupIntoRanges(rawSlots).map((r) => ({
              ...r,
              type: 'trabalho',
            })),
          }
        })

        setDays(newDays)
      } catch (err) {
        setError(err.message)
      }
      setLoading(false)
    },
    [idAgenda],
  )

  useEffect(() => {
    carregaSemana(weekStart)
  }, [weekStart, carregaSemana])

  const previousWeek = () => setWeekStart((prev) => subWeeks(prev, 1))
  const nextWeek = () => setWeekStart((prev) => addWeeks(prev, 1))

  const handleToggle = (dayIdx) => {
    setDays((prev) =>
      prev.map((d) => (d.idx === dayIdx ? { ...d, active: !d.active } : d)),
    )
  }

  const handleAddRange = (dayIdx, start, end, type) => {
    setDays((prev) => {
      const day = prev.find((d) => d.idx === dayIdx)
      if (!day) return prev
      const exists = day.ranges.some(
        (r) => r.start === start && r.end === end && r.type === type,
      )
      if (exists) return prev
      return prev.map((d) =>
        d.idx === dayIdx
          ? { ...d, ranges: [...d.ranges, { start, end, type }] }
          : d,
      )
    })
  }

  const handleRemoveRange = (dayIdx, rangeIdx) => {
    setDays((prev) =>
      prev.map((d) =>
        d.idx === dayIdx
          ? { ...d, ranges: d.ranges.filter((_, i) => i !== rangeIdx) }
          : d,
      ),
    )
  }

  const salvar = async () => {
    setSaving(true)
    try {
      const payload = buildSavePayload(days, weekStart)
      await metodoPrograma('salva', { idAgenda, ...payload }, controller)
      if (onSaved) onSaved()
      carregaSemana(weekStart)
    } catch (err) {
      setError(err.message)
    }
    setSaving(false)
  }

  const weekEnd = addDays(weekStart, 6)

  return (
    <Box sx={{ p: 2, bgcolor: '#f5f5f5', minHeight: '100%' }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h5" fontWeight="bold">
            Agenda
          </Typography>
          <AutoComplete
            metodo="buscaAgenda"
            label="Selecionar Agenda"
            value={agendaSelecionada}
            onChange={onAgendaChange}
            size="small"
            sx={{ minWidth: 250 }}
          />
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={previousWeek} size="small">
            <FaArrowLeft />
          </IconButton>
          <Typography
            variant="subtitle1"
            sx={{ minWidth: 280, textAlign: 'center' }}
          >
            {format(weekStart, "dd 'de' MMMM", { locale: ptBR })} a{' '}
            {format(weekEnd, "dd 'de' MMMM, yyyy", { locale: ptBR })}
          </Typography>
          <IconButton onClick={nextWeek} size="small">
            <FaArrowRight />
          </IconButton>
          <Button
            variant="contained"
            color="primary"
            onClick={salvar}
            disabled={loading || saving || !idAgenda}
            sx={{ ml: 2 }}
          >
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {days.map((day) => (
            <Grid key={day.idx} item xs={12} sm={6} md={4}>
              <CartaoDia
                dayName={DAY_NAMES[day.idx]}
                dateStr={format(addDays(weekStart, day.idx), 'dd/MM')}
                active={day.active}
                ranges={day.ranges}
                onToggle={() => handleToggle(day.idx)}
                onAddRange={(start, end, type) =>
                  handleAddRange(day.idx, start, end, type)
                }
                onRemoveRange={(rangeIdx) =>
                  handleRemoveRange(day.idx, rangeIdx)
                }
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  )
}

export default DisponibilidadeSemanal
